import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { PractitionerQuickLookModal } from "@common";
import api from "@api/api";
import { adminEndpoints } from "@api/endpoints";

/**
 * Thin wrapper around PractitionerQuickLookModal that owns its own state
 * so opening/closing the modal never re-renders the parent component.
 *
 * Usage:
 *   const modalRef = useRef(null);
 *   modalRef.current.open(row, { openInEdit: true, canEdit: true });
 *
 *   <PractitionerModalWrapper ref={modalRef} onSave={handleSave} />
 */
const PractitionerModalWrapper = forwardRef(function PractitionerModalWrapper({ onSave }, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const [practitioner, setPractitioner] = useState(null);
  const [openInEdit, setOpenInEdit] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const abortRef = useRef(null);

  const close = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsOpen(false);
    setPractitioner(null);
  }, []);

  const open = useCallback((row, opts = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setPractitioner({
      id: row.practitioner_id,
      name: row.practitioner_name,
      practitioner_type: row.practitioner_type,
      email: row.practitioner_email,
      associate_fee_percentage: row.associate_fee_percentage,
      clinic: row.clinic,
    });
    setOpenInEdit(!!opts.openInEdit);
    setCanEdit(opts.canEdit !== false);
    setIsOpen(true);

    api
      .get(adminEndpoints.GET_PRACTITIONER_BY_CLINIC(row.clinic, row.practitioner_id), {
        signal: controller.signal,
      })
      .then((res) => {
        if (controller.signal.aborted) return;
        setPractitioner(res.data);
      })
      .catch(() => {
        // keep the partial row data if fetch fails or was cancelled
      });
  }, []);

  useImperativeHandle(ref, () => ({ open, close }), [open, close]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  if (!practitioner) return null;

  return (
    <PractitionerQuickLookModal
      isOpen={isOpen}
      onClose={close}
      practitioner={practitioner}
      canEdit={canEdit}
      openInEdit={openInEdit}
      onSave={onSave}
    />
  );
});

export default PractitionerModalWrapper;
